import { useCallback, useState } from 'react';
import {
  Avatar,
  Button,
  Descriptions,
  Flex,
  Input,
  Modal,
  Space,
  Tag,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import {
  CheckCircleOutlined,
  CopyOutlined,
  CreditCardOutlined,
  LinkOutlined,
  RollbackOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { Payment } from '../api';
import useAppNotification from '@/hooks/useAppNotification';
import { useModule } from '@/contexts/ModuleContext';
import { sendPaymentNotificationWebhook } from '../services/notificationWebhook';
import { formModalWidth } from '@/components/ui/formModalStyles';
import { formatCurrency } from '@/utils/format';
import { getInitials } from '@/utils/userDisplay';
import { formatPaymentTimestamp, getStatusConfig, isPaymentExpired, isPaymentSettled } from './paymentStatus';

const { Text } = Typography;

interface PaymentDetailModalProps {
  /** Payment to show; the modal is open while it is set. */
  payment: Payment | null;
  onClose: () => void;
  /** Id of the payment whose URL was just copied, for the "Copied" feedback. */
  copiedPaymentUrl: number | string | null;
  onCopyUrl: (paymentUrl: string, paymentId: number) => void;
}

/**
 * Full payment breakdown: amount hero, metadata, copyable payment URL and an
 * optional "Resend Email" action when a notification webhook is configured.
 */
export function PaymentDetailModal({ payment, onClose, copiedPaymentUrl, onCopyUrl }: PaymentDetailModalProps) {
  const { token } = theme.useToken();
  const { t } = useTranslation('payment-module');
  const { showSuccessMessage, showErrorMessage } = useAppNotification();
  const [resendingEmail, setResendingEmail] = useState(false);

  const { config: moduleConfig } = useModule('payment-module');
  const notificationWebhook = moduleConfig?.config?.notificationWebhook;

  /**
   * Resends the payment notification email via the configured webhook.
   * Only available when a notificationWebhook is set on the module config.
   */
  const handleResendEmail = useCallback(async () => {
    if (!payment || !notificationWebhook) {
      if (!notificationWebhook) {
        showErrorMessage(t('details.webhookNotConfigured'));
      }
      return;
    }

    setResendingEmail(true);
    try {
      await sendPaymentNotificationWebhook(notificationWebhook, {
        paymentUrl: payment.paymentUrl,
        requestId: payment.requestId,
        amount: payment.amount,
        currency: payment.currency,
        customerEmail: payment.customerEmail,
        customerName: payment.customerName,
        description: payment.description,
        locale: payment.locale,
      });
      showSuccessMessage(t('details.emailSuccess'));
    } catch (error: unknown) {
      console.error('Failed to resend email notification:', error);
      const err = error as Record<string, any>;
      showErrorMessage(err?.message || t('details.emailError'));
    } finally {
      setResendingEmail(false);
    }
  }, [payment, notificationWebhook, showSuccessMessage, showErrorMessage, t]);

  const statusConfig = payment ? getStatusConfig(payment.status, t) : null;
  const settled = payment ? isPaymentSettled(payment.status) : false;

  return (
    <Modal
      title={
        <Flex align="center" gap={token.marginSM}>
          <Avatar
            size={40}
            style={{
              backgroundColor: token.colorPrimaryBg,
              color: token.colorPrimary,
            }}
            icon={<CreditCardOutlined />}
          />
          <div>
            <Text strong style={{ display: 'block' }}>{t('details.title')}</Text>
            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              #{payment?.requestId}
            </Text>
          </div>
        </Flex>
      }
      open={payment != null}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          {t('details.close')}
        </Button>,
        // Hide "Resend Email" for completed and refunded payments
        ...(notificationWebhook && payment && !settled
          ? [
              <Button
                key="resend"
                type="primary"
                icon={<SendOutlined />}
                onClick={handleResendEmail}
                loading={resendingEmail}
              >
                {t('details.resendEmail')}
              </Button>,
            ]
          : []),
      ]}
      width={formModalWidth(700)}
    >
      {payment && statusConfig && (
        <Space orientation="vertical" size="large" style={{ width: '100%' }}>
          {/* Amount hero card */}
          <div
            style={{
              background: `linear-gradient(135deg, ${token.colorPrimaryBg} 0%, ${token.colorBgLayout} 100%)`,
              borderRadius: token.borderRadiusLG,
              padding: token.paddingLG,
              textAlign: 'center',
            }}
          >
            <Text type="secondary">{t('details.paymentAmount')}</Text>
            <div style={{ marginTop: token.marginXS }}>
              <Text strong style={{ fontSize: token.fontSizeHeading2, color: token.colorPrimary }}>
                {formatCurrency(payment.amount, payment.currency)}
              </Text>
            </div>
            {payment.status.toLowerCase() === 'refunded' && payment.refundedAmount != null && (
              <div style={{ marginTop: token.marginSM }}>
                <Tag color="orange" icon={<RollbackOutlined />}>
                  {t('status.refunded')}: {formatCurrency(payment.refundedAmount, payment.currency)}
                </Tag>
              </div>
            )}
            <div style={{ marginTop: token.marginSM }}>
              <Tag
                color={statusConfig.color}
                icon={statusConfig.icon}
                style={{ fontSize: token.fontSize, padding: `${token.paddingXXS}px ${token.paddingSM}px` }}
              >
                {statusConfig.label}
              </Tag>
            </div>
          </div>

          {/* Payment metadata */}
          <Descriptions
            bordered
            column={{ xs: 1, sm: 2 }}
            size="small"
            items={[
              {
                label: t('details.paymentId'),
                children: <Text code>{payment.id}</Text>,
              },
              {
                label: t('details.requestId'),
                children: <Tag color="geekblue">{payment.requestId}</Tag>,
              },
              {
                label: t('details.customer'),
                children: (
                  <Flex gap={8} align="center">
                    <Avatar size="small" style={{ backgroundColor: token.colorPrimaryBg, color: token.colorPrimary }}>
                      {getInitials(payment.customerName)}
                    </Avatar>
                    <Text>{payment.customerName}</Text>
                  </Flex>
                ),
                span: 2,
              },
              {
                label: t('details.email'),
                children: <Text copyable>{payment.customerEmail}</Text>,
                span: 2,
              },
              {
                label: t('details.description'),
                children: (
                  <Text type={payment.description ? undefined : 'secondary'}>
                    {payment.description || t('details.noDescription')}
                  </Text>
                ),
                span: 2,
              },
              {
                label: t('details.gateway'),
                children: <Tag color="purple">{payment.gateway.toUpperCase()}</Tag>,
              },
              {
                label: t('details.locale'),
                children: <Text>{payment.locale.toUpperCase()}</Text>,
              },
              {
                label: t('details.created'),
                children: formatPaymentTimestamp(payment.createdAt),
              },
              {
                label: t('details.expires'),
                children: (
                  <Text type={isPaymentExpired(payment.expiresAt) ? 'danger' : undefined}>
                    {formatPaymentTimestamp(payment.expiresAt)}
                    {isPaymentExpired(payment.expiresAt) && (
                      <Tag color="error" style={{ marginLeft: 8 }}>{t('details.expired')}</Tag>
                    )}
                  </Text>
                ),
              },
            ]}
          />

          {/* Copyable payment URL — hidden for completed and refunded payments */}
          {!settled && (
            <div>
              <Text type="secondary" style={{ marginBottom: token.marginXS, display: 'block' }}>
                {t('details.paymentUrl')}
              </Text>
              <Space.Compact style={{ width: '100%' }}>
                <Input
                  value={payment.paymentUrl}
                  readOnly
                  prefix={<LinkOutlined style={{ color: token.colorPrimary }} />}
                  style={{
                    fontFamily: 'monospace',
                    fontSize: token.fontSizeSM,
                  }}
                />
                <Tooltip title={copiedPaymentUrl === payment.id ? t('actions.copied') : t('details.copyUrl')}>
                  <Button
                    type="primary"
                    icon={copiedPaymentUrl === payment.id ? <CheckCircleOutlined /> : <CopyOutlined />}
                    onClick={() => onCopyUrl(payment.paymentUrl, payment.id)}
                  />
                </Tooltip>
              </Space.Compact>
            </div>
          )}
        </Space>
      )}
    </Modal>
  );
}

export default PaymentDetailModal;
